import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from "react-router-dom";
import Grid from '@mui/material/Grid2';
import { v4 as uuidv4 } from "uuid";
import {
  Character, 
} from "@dndModels";
import { 
  ModelBase, 
  PlayerObject 
} from "@models";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import ObjectIndex from "@components/Navigation/ObjectIndex";
import ShareButton from "@components/input/buttons/ShareButton";
import ToastBox from "@components/display/ToastBox";
import AddButton from "@components/input/buttons/AddButton";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterIndex() {
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [toast_time, setToastTime] = useState(-1);

  const removeDeletedCharCookies = useCallback(() => {
    // Also remove char cookies for archived characters.
    api.getObjectsOld("character", { owner_id: (api.user ? api.user._id : "No Login") }).then(res => {
      const chars = res as Character[];
      const good_char_ids = chars.filter(o => !o.is_archived).map(o => o._id);
      const items: any = { ...localStorage };
      let bad_item_keys = Object.keys(items).filter(o => o.startsWith("character_"));
      for (const char_id of good_char_ids) {
        bad_item_keys = bad_item_keys.filter(o => !o.startsWith(`character_${char_id}`));
      }

      if (bad_item_keys.length > 0) {
        console.log("Deleting Character Cookies for deleted or archived characters");
        console.log(bad_item_keys);
        for (const key of bad_item_keys) {
          localStorage.removeItem(key);
        }
      }
    });
  }, []);

  useEffect(() => {
    if (api.user) {
      removeDeletedCharCookies();
    }
  }, [])

  const copyCharacter = (obj: Character) => {
    const user = api.user;
    if (user) {
      char_util.getCharacter({id: obj._id}).then(res => {
        if (res) {
          // debugger;
          const new_obj = res.clone(true);
          new_obj.name = new_obj.name + " (Copy)";
          new_obj._id = "";
          new_obj.owner_id = user._id;
          api.createCharacter(new_obj).then(res => {
            setProcessing(false);
            setNavigateTo(`/character/${res.body.id}`);
          });
        }
      });
    }
  }

  const updateCharacter = (obj: Character, onSuccess: Function, no_cookie = false) => {
    const user = api.user;
    if (user) {
      char_util.getCharacter({id: obj._id}).then(res => {
        if (res) {
          res.is_favorite = obj.is_favorite;
          res.is_archived = obj.is_archived;
          res.shared_id = obj.shared_id;
          api.updateCharacter(res, ["main"], true).then(res => {
            if (no_cookie) {
              api.removeCharacterCookies(obj._id);
              setProcessing(false);
              onSuccess();
            } else {
              char_util.getCharacterFromCookie(obj._id).then(res => {
                const cookie_char = res;
                if (cookie_char !== null) {
                  cookie_char.is_favorite = obj.is_favorite;
                  cookie_char.is_archived = obj.is_archived;
                  cookie_char.shared_id = obj.shared_id;
                  api.updateCharacter(cookie_char, ["main"]).then(() => {
                    setProcessing(false);
                    onSuccess();
                  });
                } else {
                  setProcessing(false);
                  onSuccess();
                }
              });
            }
          });
        }
      });
    }
  }

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (api.user === null) {
    return (
      <span className="header">You have to log in to use this page.</span>
    );
  } else {
    return (
      <Grid container spacing={0} direction="row">
        { !api.online && 
          <Grid size={{ xs: 12 }}>
            Only Characters that you have stored in your browser cookies are available while offline.
          </Grid>
        }
        <Grid size={{ xs: 12 }}>
          <ObjectIndex 
            data_type="character"
            quick_fields={["game_class","subclass","race","subrace"]}
            true_filter={{ owner_id: (api.user ? api.user._id : "No Login") }}
            exclude_options={["Edit"]}
            disabled={processing}
            show_image
            special_copy={(obj: ModelBase | PlayerObject) => {
              if (obj instanceof Character) {
                copyCharacter(obj);
              }
            }}
            special_update={(obj: ModelBase | PlayerObject, onSuccess: Function) => {
              if (obj instanceof Character) {
                updateCharacter(obj, onSuccess, true);
              }
            }}
            onDelete={(_id: string) => {
              api.removeCharacterCookies(_id);
            }}
            extra_buttons={(obj: ModelBase | PlayerObject) => {
              return (
                <ShareButton
                  disabled={processing}
                  onClick={() => {
                    try {
                      if (obj instanceof Character) {
                        const char = obj;
                        if (char.shared_id === "") {
                          char.shared_id = uuidv4().toString();
                          updateCharacter(char, () => {
                            navigator.clipboard.writeText(`${window.location.origin}/character/shared/${char.shared_id}`);
                            setToastTime(common_util.get_time_number());
                          });
                        } else {
                          navigator.clipboard.writeText(`${window.location.origin}/character/shared/${char.shared_id}`);
                          setToastTime(common_util.get_time_number());
                        }
                      }
                    } catch (ex) {
                      console.error(ex);
                    }
                  }}
                />
              );
            }}
            onAdd={() => {
              if (api.user) {
                setProcessing(true);
                const obj = new Character();
                console.log(obj);
                obj.owner_id = api.user._id;
                const sb_ids = api.user.source_book_ids;
                obj.character_options.source_books = api.source_books.filter(o => 
                  !o.homebrew && !o.ua && !o.third_party && sb_ids.includes(o._id)).map(o => o._id);
                api.createCharacter(obj).then(res => {
                  if (res.body && !res.body.error) {
                    setProcessing(false);
                    setNavigateTo(`/character/${res.body.id}`);
                  }
                });
              }
            }}
            extra_by_add={() => {
              return (
                <>
                  &nbsp;
                  <AddButton
                    label="Add Sidekick"
                    disabled={processing || !api.online}
                    onClick={() => {
                      if (api.user) {
                        setProcessing(true);
                        const obj = new Character();
                        obj.owner_id = api.user._id;
                        obj.sidekick = true;
                        const sb_ids = api.user.source_book_ids;
                        obj.character_options.source_books = api.source_books.filter(o => 
                          !o.homebrew && !o.ua && !o.third_party && sb_ids.includes(o._id)).map(o => o._id);
                        api.createCharacter(obj).then(res => {
                          if (res.body && !res.body.error) {
                            setProcessing(false);
                            setNavigateTo(`/character/${res.body.id}`);
                          }
                        });
                      }
                    }}
                  />
                </>
              );
            }}
          />
        </Grid>
        <ToastBox
          openTime={ toast_time }
        >
          Share Link copied to Clipboard
        </ToastBox>
      </Grid>
    ); 
  } 
}

